import { LoadingStageModel } from '@kts-front/models';
import { debounce } from '@kts-front/utils';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';

import { OptionEntity } from 'types/OptionEntity';

import { FieldStore } from '../FieldStore';

import { FieldSelectStoreOptions } from './types';

type PrivateFields = '_options' | '_searchValue';

export class BaseFieldSelectStore<V extends OptionEntity = OptionEntity> extends FieldStore<V[]> {
  readonly meta = new LoadingStageModel();
  protected readonly _getOptions: FieldSelectStoreOptions<V>['getOptions'];

  protected _options: V[] = [];
  total: number | null = null;
  /** Количество опций без учёта текущего поискового запроса, используется чтобы понять, есть ли у поля опции вообще */
  baseTotal: number | null = null;
  protected _searchValue = '';

  private _debounceLoad: () => void;

  constructor({ getOptions, searchDebounceTimeout, ...options }: FieldSelectStoreOptions<V>) {
    super({ ...options, defaultValue: options.defaultValue || [] });

    this._getOptions = getOptions;

    this._debounceLoad = debounce({
      func: () => {
        this.load(true);
      },
      timeout: searchDebounceTimeout ?? 450,
    });

    makeObservable<BaseFieldSelectStore<V>, PrivateFields>(this, {
      total: observable,
      baseTotal: observable,
      load: action.bound,
      _options: observable.ref,
      _searchValue: observable,
      options: computed,
      setOptions: action.bound,
      resetOptions: action.bound,
      searchValue: computed,
      setSearchValue: action.bound,
      resetSearch: action.bound,
      valueKeysSet: computed,
      valueKeys: computed,
      optionsMap: computed,
      removeByKey: action.bound,
    });

    this._requiredDisposers.push(
      reaction(
        () => this.searchValue,
        () => {
          this.setOptions([]);
          this.meta.loading();

          return this._debounceLoad();
        },
      ),
    );
  }

  get options(): V[] {
    return this._options;
  }

  get optionsMap(): Record<V['key'], V> {
    return this.options.reduce(
      (acc, item) => {
        acc[item.key as V['key']] = item;

        return acc;
      },
      {} as Record<V['key'], V>,
    );
  }

  get valueKeysSet(): Set<V['key']> {
    return new Set(this.valueKeys);
  }

  get valueKeys(): V['key'][] {
    return this.value.map(({ key }) => key);
  }

  setOptions(value: V[]): void {
    this._options = value;
  }

  resetOptions(): void {
    this._options = [];
  }

  get searchValue(): string {
    return this._searchValue;
  }

  setSearchValue(value: string): void {
    this._searchValue = value;
  }

  resetSearch(): void {
    this._searchValue = '';
  }

  removeByKey(key: V['key']): void {
    this.setValue(this.value.filter((item) => item.key !== key));
  }

  async load(reload = false): Promise<void> {
    if (!reload && !this.canLoadMore) {
      return;
    }

    this.meta.loading();

    const response = await this._getOptions({
      limit: 25,
      offset: reload ? 0 : this.options.length,
      search: this.searchValue,
      keys: [],
    });

    if (response.isError) {
      this.meta.error();

      return;
    }

    runInAction(() => {
      if (reload) {
        this.setOptions(response.data.items);
      } else {
        this.setOptions([...this.options, ...response.data.items]);
      }

      this.total = response.data.total;

      if (!this.searchValue) {
        this.baseTotal = response.data.total;
      }

      this.meta.success();
    });
  }

  get canLoadMore(): boolean {
    if (this.total === null) {
      return true;
    }

    if (this.meta.isLoading) {
      return false;
    }

    return this.options.length < this.total;
  }

  /**
   * По массиву ключей загружает опции и обновляет значение поля
   */
  syncValueByKeys = async (_keys: V['key'][]) => {
    if (!_keys || _keys.length === 0) {
      return;
    }

    const keys = _keys.map(String);
    const keysSet = new Set(keys);
    const selectedOptions = this.options.filter((option) => keysSet.has(option.key));

    if (selectedOptions.length > 0) {
      this.setValue(selectedOptions, { noTouch: true });

      return;
    }

    const response = await this._getOptions({ offset: 0, limit: keys.length, search: '', keys });

    if (response.isError || response.data.items.length === 0) {
      return;
    }

    this.setValue(response.data.items, { noTouch: true });
  };

  destroy(): void {
    this._requiredDisposers.forEach((d) => d());
  }
}
